import { Ajax } from "../../Common/Ajax";
import { CookieHelper } from "../../Common/CookieHelper";
import { GlobalData } from "../../Common/GlobalData";
import { Notification, PortfolioGridField, PortfolioGridFilter, TemplateHelpers } from "../../Common/KendoHelpers";
import { MultiSourceAnalysis } from "../../MultiSource/MultiSourceAnalysis";

export class WatchList extends MultiSourceAnalysis {
    constructor() {
        super('#watchListGrid');
    }

    public initWithWatchListIds(initWatchListIds: number[] = []) {
        const dataSourceReadUrl = this.getDataSourceReadUrl(initWatchListIds);
        super.init(dataSourceReadUrl);
    }

    protected override initFields() {
        super.initFields();

        this.fields['daysToEarnings'] = new PortfolioGridField('daysToEarnings');
        this.fields['risk'] = new PortfolioGridField('risk');
        this.fields['ownedInOtherAccount'] = new PortfolioGridField('ownedInOtherAccount');
        this.fields['issueNo'] = new PortfolioGridField('issueNo');
        this.fields['readyList'] = new PortfolioGridField('readyList');
        this.fields['readyToBuyList'] = new PortfolioGridField('readyToBuyList');
        this.fields['buyNow'] = new PortfolioGridField('buyNow');

        this.fields['readyList'].addFilter(MultiSourceAnalysis.FILTER_WEEKLY,
            new PortfolioGridFilter('readyList', 'eq', true));

        this.fields['readyList'].addFilter(MultiSourceAnalysis.FILTER_MONTHLY,
            new PortfolioGridFilter('readyList', 'eq', true));
    }

    protected override getGridDataSource(): any {
        const baseDataSource = super.getGridDataSource();

        return {
            ...baseDataSource,
            schema: {
                ...baseDataSource.schema,
                model: {
                    ...baseDataSource.schema.model,
                    fields: {
                        ...baseDataSource.schema.model.fields,
                        daysToEarnings: { type: 'number' },
                        risk: { type: 'number' },
                        ownedInOtherAccount: { type: 'boolean' },
                        issueNo: { type: 'number' },
                        readyList: { type: 'boolean' },
                        readyToBuyList: { type: 'boolean' },
                        buyNow: { type: 'boolean' }
                    }
                }
            }
        };
    }

    protected override getGridOptions(dataSource: any): kendo.ui.GridOptions {
        const options = super.getGridOptions(dataSource);
        options.dataBound = () => {
            super.gridDataBound();

            // Show or hide the IssueNo column depending on whether it contains data.
            const data = this.grid.dataSource.view();

            if (!data || data.length === 0) {
                this.grid.hideColumn(this.fields['issueNo'].getName());
                return;
            }

            // Ideally we'd check all the rows, but this page already loads slowly.
            const firstRow = data[0];

            if (firstRow.issueNo == null) {
                this.grid.hideColumn(this.fields['issueNo'].getName());
            } else {
                this.grid.showColumn(this.fields['issueNo'].getName());
            }

            // Refresh the grid and switch to the the default industry source.
            $(this.gridId).show();
            this.switchCompanyAndIndustry(true);
        };

        options.columns!.push({
            field: this.fields['daysToEarnings'].getName(),
            title: 'Days<br>to<br>Earnings',
            filterable: true,
            width: '4%',
            headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
            attributes: { 'class': 'table-cell grid-cell-center' },
        }, {
            field: this.fields['risk'].getName(),
            title: 'Risk<br>%',
            filterable: false,
            width: '4%',
            headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
            attributes: { 'class': 'table-cell grid-cell-center' },
            format: '{0:p0}',
        }, {
            field: this.fields['ownedInOtherAccount'].getName(),
            title: 'Own<br>Other<br>Acct',
            filterable: true,
            width: '4%',
            headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
            attributes: { 'class': 'table-cell grid-cell-center' },
            template: kendo.template(TemplateHelpers.getYellowBoolTemplate('ownedInOtherAccount', 'Y')),
        }, {
            field: this.fields['issueNo'].getName(),
            title: 'Issue<br>No',
            filterable: true,
            width: '4%',
            headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
            attributes: { 'class': 'table-cell grid-cell-center' },
        }, {
            field: this.fields['readyList'].getName(),
            title: 'Like<br>List',
            filterable: true,
            width: '4%',
            headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
            template: kendo.template(TemplateHelpers.getGreenBoolTemplate('readyList', 'Y')),
        }, {
            field: this.fields['readyToBuyList'].getName(),
            title: 'Like<br>to<br>Buy',
            filterable: true,
            width: '4%',
            headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
            template: kendo.template(TemplateHelpers.getCheckboxTemplate('readyToBuyList')),
        }, {
            field: this.fields['buyNow'].getName(),
            title: 'Buy<br>Now',
            filterable: true,
            width: '4%',
            headerAttributes: { 'class': 'table-header-cell grid-header-12 table-header-cell-space-for-filter' },
            attributes: { "class": "table-cell", style: "text-align: center; font-size: 12px" },
            template: kendo.template(TemplateHelpers.getCheckboxTemplate('buyNow')),
        });

        return options;
    }

    protected override defineEvents() {
        super.defineEvents();

        // Update value of the field when user clicks a checkbox.
        $('.readyToBuyListCheckbox').off('click').on('click', (e: any) => {
            this.onReadyToBuyClick(e);
        });
        $('.buyNowCheckbox').off('click').on('click', (e: any) => {
            this.onBuyNowClick(e);
        });
    }

    public updateDataSource(watchListIds: number[]): void {
        const dataSource = super.getGridDataSource();
        dataSource.transport.read = this.getDataSourceReadUrl(watchListIds);
        this.grid.setDataSource(dataSource);
    }

    private getDataSourceReadUrl(watchListIds: number[]): string {
        const dataSource = super.getGridDataSource();
        let newUrl = dataSource?.transport.read;
        if (!(newUrl?.length > 0)) {
            newUrl = GlobalData.URL_BASE +
                GlobalData.URL_SUFFIX_PORTFOLIO_ACCOUNT_MANAGER_WATCHLIST +
                '?account=' +
                CookieHelper.get(GlobalData.PORTFOLIO_ACCOUNT_SELECTOR_COOKIE);
        }
        else {
            const atPos = newUrl.indexOf('&');
            if (atPos != -1) {
                newUrl = dataSource.transport.read.substr(0, atPos);
            }
        }

        for (const watchListId of watchListIds) {
            newUrl += `&watchListIds=${watchListId}`;
        }

        return newUrl;
    }

    private onReadyToBuyClick(e: any) {
        const dataItem = this.getClosestDataItem(e) as any;
        dataItem.readyToBuyList = e.target.checked;

        this.updateReadyToBuy(dataItem);
    }

    private updateReadyToBuy(dataItem: any): JQueryPromise<boolean> {
        const deferred = jQuery.Deferred();

        const success = () => {
            this.grid.dataSource.read();
            deferred.resolve(true);
        };

        const error = (x: any) => {
            const msg = new Notification();
            msg.error(x.responseJSON);
            deferred.resolve(false);
        };

        Ajax.invoke(GlobalData.URL_SUFFIX_PORTFOLIO_ACCOUNT_MANAGER_WATCHLIST +
            '/ReadyToBuyList?account=' + CookieHelper.get(GlobalData.PORTFOLIO_ACCOUNT_SELECTOR_COOKIE),
            'PUT', dataItem, success, error);

        return deferred;
    }

    private onBuyNowClick(e: any) {
        const dataItem = this.getClosestDataItem(e) as any;
        dataItem.buyNow = e.target.checked;

        // If this symbol isn't in the readyToBuyList, and they want to buy now,
        // add it to the readyToBuyList first.
        if (!dataItem.readyToBuyList && dataItem.buyNow) {
            dataItem.readyToBuyList = true;
            $.when(this.updateReadyToBuy(dataItem)).done((success: boolean) => {
                if (success) {
                    this.updateBuyNow(dataItem);
                }
            });
        }
        else {
            this.updateBuyNow(dataItem);
        }
    }

    private updateBuyNow(dataItem: any) {
        const success = () => {
            this.grid.dataSource.read();
        };

        const error = (x: any) => {
            const msg = new Notification();
            msg.error(x.responseJSON);
        };

        Ajax.invoke(GlobalData.URL_SUFFIX_PORTFOLIO_ACCOUNT_MANAGER_READY_TO_BUY_LIST +
            '/BuyNow?account=' + CookieHelper.get(GlobalData.PORTFOLIO_ACCOUNT_SELECTOR_COOKIE) +
            '&symbol=' + dataItem.symbol + '&buyNow=' + dataItem.buyNow,
            'PUT', dataItem, success, error);
    }

    private getClosestDataItem(e: any) {
        return this.grid.dataItem($(e.target).closest('tr'));
    }
}
